import { AxiosError } from 'axios';
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import {
  fetchToBeTransferredBySearchKey,
  fetchTransfersByMonthlyWithSearchKey,
  fetchTransfersByQuarter,
  fetchTransfersByQuarterWithSearchKey,
  fetchTransfersToBe,
  fetchTransferStoriesByTransferId,
  IFetchTransfersParams,
  TransferSearchType,
  updateTransferStatus,
} from '../../services/api/Transfer';
import { IPaginationResponseDto, ITransfer, ITransferInformation } from '../../services/types';
import useBackdrop from '../useBackdrop';
import useSnackbar from '../useSnackbar';

enum QueryKey {
  Transfers = 'allTransfers',
  TransferStoriesByTransferId = 'transferStoriesByTransferId',
  TransfersBySearchKey = 'transferBySearchKey',
  TransferByMonthlyWithSearchKey = 'transferByMonthlyWithSearchKey',
}

interface ITransferStatusUpdateParams {
  transferId: number;
  quarter: number;
  searchType?: TransferSearchType;
  searchKey?: number;
}

interface ITransferMutation {
  setTransferred: UseMutationResult<ITransfer, Error, ITransferStatusUpdateParams>;
}

const commonErrorHandler = {
  onError: (e: AxiosError<any>) => {
    console.log('ERROR', e.response ? e.response.data.message : e.message);
  },
  keepPreviousData: true,
};

export const useTransfers = (quarter: number): UseQueryResult<ITransfer[], AxiosError> =>
  useQuery<ITransfer[], AxiosError>(
    [QueryKey.Transfers, { quarter }],
    async () => {
      const [fetchPromise] = quarter === 0 ? fetchTransfersToBe() : fetchTransfersByQuarter(quarter);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    },
    commonErrorHandler,
  );

export const useTransfersWithSearchKey = (quarter: number, params: IFetchTransfersParams): UseQueryResult<ITransfer[], AxiosError> =>
  useQuery<ITransfer[], AxiosError>(
    [QueryKey.TransfersBySearchKey, { quarter, params }],
    async () => {
      const [fetchPromise] = quarter === 0 ? fetchToBeTransferredBySearchKey(quarter, params) : fetchTransfersByQuarterWithSearchKey(quarter, params);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    },
    commonErrorHandler,
  );

export const useTransferMonthlyWithSearchKey = (
  month: number,
  page: number,
  limit: number,
  params: IFetchTransfersParams,
): UseQueryResult<IPaginationResponseDto<ITransfer>, AxiosError> =>
  useQuery<IPaginationResponseDto<ITransfer>, AxiosError>(
    [QueryKey.TransferByMonthlyWithSearchKey, { month, page, limit, params }],
    async () => {
      const [fetchPromise] = fetchTransfersByMonthlyWithSearchKey(month, page, limit, params);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    },
    commonErrorHandler,
  );

export const useTransferStories = (transferId: number): UseQueryResult<ITransferInformation, AxiosError> =>
  useQuery<ITransferInformation | never[], AxiosError>(
    [QueryKey.TransferStoriesByTransferId, { transferId }],
    async () => {
      if (transferId === 0) {
        return [];
      }
      const [fetchPromise] = fetchTransferStoriesByTransferId(transferId);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    },
    { ...commonErrorHandler },
  ) as UseQueryResult<ITransferInformation, AxiosError>;

export const useTransferMutation = (): ITransferMutation => {
  const snackbar = useSnackbar();
  const backdrop = useBackdrop();
  const queryClient = useQueryClient();

  const setTransferred = useMutation<ITransfer, AxiosError, ITransferStatusUpdateParams>(
    async ({ transferId, quarter }) => {
      backdrop.show();
      const { data: apiResult } = await updateTransferStatus(transferId);

      if (!apiResult.success) {
        throw new Error(apiResult.message);
      }

      return apiResult.data;
    },
    {
      onSuccess: (data, variables) => {
        snackbar.success('Updated successfully.');
        queryClient.setQueryData(
          [
            QueryKey.TransfersBySearchKey,
            { quarter: variables.quarter, params: { searchType: variables.searchType, searchKey: variables.searchKey } },
          ],
          old => {
            return (old as ITransfer[]).map(transfer => (transfer.id === data.id ? { ...transfer, transferredAt: data.transferredAt } : transfer));
          },
        );
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  return {
    setTransferred,
  };
};
